/**
 * ChestBackpackBonusIndicator Component
 *
 * Displays a hoverable "2x POINTS" indicator on the Chest page
 * Shows campaign details on hover including:
 * - Time remaining
 * - Points progress (X/20,000)
 * - Call to action if not using Backpack
 *
 * Positioned above the chest slider bar, left-aligned
 */

import { useState } from 'react';

import { useBackpackCampaign } from '@/hooks/useBackpackCampaign';

function formatTimeRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Campaign ended';
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return `${days}d ${hours}h left`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m left`;
  }
  return `${minutes}m left`;
}

export function ChestBackpackBonusIndicator() {
  const { campaignData, isLoading } = useBackpackCampaign();
  const [isHovered, setIsHovered] = useState(false);

  // Only show if user is connected with Backpack wallet and hasn't reached the cap
  if (isLoading || !campaignData?.isActive || campaignData?.walletProvider !== 'Backpack' || campaignData?.remainingBonus <= 0) {
    return null;
  }

  const isEligible = campaignData.isEligible && campaignData.remainingBonus > 0;
  const timeRemaining = formatTimeRemaining(campaignData.endDate);
  const progress = `${campaignData.userBonusEarned.toLocaleString()}/${campaignData.maxBonus.toLocaleString()}`;

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Backpack 2x Image */}
      <div
        className="cursor-pointer transition-all duration-200"
        style={{
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
        }}
      >
        <img
          src="/backpack2x.svg"
          alt="Backpack 2x Points"
          className="h-10 w-auto"
        />
      </div>

      {/* Hover Tooltip */}
      {isHovered && (
        <div
          className="absolute top-full left-0 mt-2 w-72 p-4 rounded-xl shadow-2xl z-50 animate-slide-in"
          style={{
            backgroundColor: '#343434',
            border: '2px solid #50E4F9',
            boxShadow: '0 0 20px rgba(80, 228, 249, 0.3)',
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-3">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                fill="#50E4F9"
              />
            </svg>
            <h3
              className="text-base font-bold"
              style={{ color: '#50E4F9', fontFamily: 'Outfit, sans-serif' }}
            >
              Backpack Campaign
            </h3>
          </div>

          {/* Content */}
          <div className="space-y-3">
            {isEligible ? (
              <>
                {/* For eligible users */}
                <p className="text-sm" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                  You're earning <strong style={{ color: '#50E4F9' }}>2x points</strong> on every chest opening!
                </p>

                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between text-xs mb-1" style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>
                    <span>Bonus Progress</span>
                    <span style={{ color: '#50E4F9', fontWeight: 600 }}>{progress} pts</span>
                  </div>
                  <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'rgba(80, 228, 249, 0.2)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${(campaignData.userBonusEarned / campaignData.maxBonus) * 100}%`,
                        backgroundColor: '#50E4F9',
                      }}
                    />
                  </div>
                </div>

                {/* Time Remaining */}
                <div className="flex justify-between items-center text-xs pt-2" style={{ borderTop: '1px solid rgba(80, 228, 249, 0.3)' }}>
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Campaign ends in</span>
                  <span style={{ color: '#50E4F9', fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </>
            ) : (
              <>
                {/* For non-Backpack users */}
                <p className="text-sm" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                  Connect with <strong style={{ color: '#FB8B00' }}>Backpack wallet</strong> to earn 2x points on every chest opening!
                </p>

                {/* Call to Action */}
                <div
                  className="p-3 rounded-lg text-center"
                  style={{
                    background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                  }}
                >
                  <p className="text-xs font-bold" style={{ color: '#000000', fontFamily: 'Outfit, sans-serif' }}>
                    Limited Time: Up to 20,000 bonus points!
                  </p>
                </div>

                {/* Time Remaining */}
                <div className="flex justify-between items-center text-xs pt-2" style={{ borderTop: '1px solid rgba(251, 139, 0, 0.3)' }}>
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Offer ends in</span>
                  <span style={{ color: '#FB8B00', fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
